import { useNavigate } from 'react-router-dom';
import { NotificationAction, UserScopedNotification } from '@acp/shared';
import {
    differenceInHours, format, parseISO,
} from 'date-fns';
import { useMarkAsRead } from './use-notifications';

type NotificationActionTarget = {
    url: string;
    openInNewTab: boolean;
};

function getActionTarget(action: NotificationAction): NotificationActionTarget | null {
    switch (action.type) {
        case 'view_property':
        case 'view_booking':
            return { url: `/property/${action.propertyId}`, openInNewTab: true };
        case 'view_quotation':
            return { url: `/view-quote/${action.quotationId}`, openInNewTab: true };
        case 'view_minimum_standards_assessment_report':
            // For now, deep-link to the "recently completed" tab of the minimum standards page.
            return { url: '/minimum-standards?tab=recently-completed', openInNewTab: true };
        case 'view_service_report':
            return { url: `/view-service-report/${action.serviceSheetId}`, openInNewTab: true };
        default:
            return null;
    }
}

// Performs the action when clicked and marks the notification as read.
function useNotificationItem(notification: UserScopedNotification) {
    const navigate = useNavigate();
    const markAsRead = useMarkAsRead();

    const onClick = () => {
        if (notification.isRead === false) {
            markAsRead.mutate(notification.id);
        }

        const target = getActionTarget(notification.action);
        if (target === null) return;

        if (target.openInNewTab) {
            window.open(target.url, '_blank', 'noopener,noreferrer');
        } else {
            navigate(target.url);
        }
    };

    const onMarkAsReadClick = () => {
        markAsRead.mutate(notification.id);
    };

    const getNotifiedAt = () => {
        const notifiedDate = format(parseISO(notification.notifiedAt), 'd/M/yyyy');
        const notifiedTime = format(parseISO(notification.notifiedAt), 'h:mm a');
        const isLessThanADayAgo = differenceInHours(
            new Date(),
            parseISO(notification.notifiedAt)
        ) < 24;

        if (isLessThanADayAgo) {
            return (`" at ${notifiedTime}"`);
        }

        return (`" on ${notifiedDate} at ${notifiedTime}"`);
    };

    return {
        onClick,
        onMarkAsReadClick,
        getNotifiedAt,
    };
}

export {
    useNotificationItem,
    getActionTarget,
};
